/**
 * UserMenuItems - Menu items for the user dropdown
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../design-system';
import { ChangePasswordModal } from '../ChangePasswordModal';

// Helper component for menu items
const _MenuItem: React.FC<{
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  className?: string;
}> = ({ onClick, icon, label, className = '' }) => (
  <Button
    onClick={onClick}
    variant="ghost"
    size="md"
    fullWidth
    className={`!justify-start text-left !px-4 ${className}`}
    leftIcon={icon}
  >
    {label}
  </Button>
);

interface UserMenuItemsProps {
  onClose: () => void;
  onLogout: () => void;
}

// Navigation menu items data
// All routes should use /app prefix since they're under protected routes
const menuItems = [
  {
    path: '/app/profile',
    icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
    label: 'Profile'
  },
  {
    path: '/app/recipes/import-export',
    icon: 'M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10',
    label: 'Import/Export'
  },
  {
    path: '/app/settings',
    icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
    label: 'Settings'
  }
];

// Security menu items (separate section)
const securityMenuItems = [
  {
    action: 'change-password',
    icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
    label: 'Change Password'
  }
];

export const UserMenuItems: React.FC<UserMenuItemsProps> = ({ onClose, onLogout }) => {
  const navigate = useNavigate();
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleSecurityAction = (action: string) => {
    switch (action) {
      case 'change-password':
        setShowChangePasswordModal(true);
        break;
      default:
        break;
    }
  };

  const handlePasswordChangeSuccess = () => {
    setShowChangePasswordModal(false);
    onClose();
  };

  return (
    <>
      {/* Navigation Menu Items */}
      <div className="py-1">
        {menuItems.map((item) => (
          <_MenuItem
            key={item.path}
            onClick={() => handleNavigate(item.path)}
            icon={
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
              </svg>
            }
            label={item.label}
          />
        ))}
      </div>

      {/* Security Menu Items */}
      <div className="border-t border-gray-100 py-1">
        {securityMenuItems.map((item) => (
          <_MenuItem
            key={item.action}
            onClick={() => handleSecurityAction(item.action)}
            icon={
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
              </svg>
            }
            label={item.label}
          />
        ))}
      </div>

      {/* Sign Out */}
      <div className="border-t border-gray-100 py-1">
        <_MenuItem
          onClick={onLogout}
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" 
              />
            </svg>
          }
          label='Sign Out'
          className="text-red-600 hover:bg-red-50"
        />
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        onSuccess={handlePasswordChangeSuccess}
      />
    </>
  );
};